/**
 * BR currency helpers used by the job-compensation form.
 *
 * Storage contract: salary values travel as integer cents end-to-end so
 * float drift can't change a candidate's offer (R$ 50,01 ≠ 5001 cents).
 * The form keeps strings (operator types '5.000,00') and we convert at
 * the boundary on hydrate / submit.
 */

const BR_CURRENCY = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Parse a BR-formatted currency string ('5.000,00', 'R$ 5.000,00', '5000,5')
 * into integer cents. Returns null when the input is empty or unparseable.
 */
export function parseBrCurrencyToCents(value: string): number | null {
  if (!value) return null;
  const normalized = value
    .replace(/\s/g, '')
    .replace(/[R$]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  if (normalized === '') return null;
  const reais = Number(normalized);
  if (!Number.isFinite(reais) || reais < 0) return null;
  return Math.round(reais * 100);
}

/**
 * Render integer cents as a BR-formatted decimal string ('5000,00' → not
 * prefixed with R$; the form's <Input> already shows the symbol next to it).
 * Returns '' for null/undefined so the form's controlled input stays empty.
 */
export function formatCentsToBrInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return BR_CURRENCY.format(cents / 100);
}

/**
 * Live BRL input mask. Strips all non-digit chars, treats the remaining
 * digits as integer cents, then re-formats. So typing "5", "50", "500",
 * "5000" displays "0,05", "0,50", "5,00", "50,00" — the cents fill from
 * the right, mirroring the way BR currency inputs feel on banking sites.
 *
 * Empty input returns ''. Cap at 12 digits (R$ 9.999.999.999,99) to avoid
 * `parseInt` precision drift on absurd inputs.
 */
export function maskBrCurrencyInput(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 12);
  if (!digits) return '';
  const cents = Number.parseInt(digits, 10);
  if (!Number.isFinite(cents)) return '';
  return BR_CURRENCY.format(cents / 100);
}
